import { useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import GlassInput from '../ui/GlassInput';
import Field from '../ui/Field';
import { createTipoConcepto } from '../../services/tipoConceptos';

const ACRONYM_REGEX = /^[A-ZÀ-ÿ]$/;
const INITIAL_VALUES = { acronym: '', name: '' };

function validateAcronym(value) {
  const trimmed = value.trim();
  if (!trimmed) return 'El acrónimo es obligatorio.';
  if (!ACRONYM_REGEX.test(trimmed)) {
    return 'Usa una sola letra mayúscula (ej. I, E, A).';
  }
  return null;
}

function validateName(value) {
  const trimmed = value.trim();
  if (!trimmed) return 'El nombre del tipo de concepto es obligatorio.';
  if (trimmed.length < 2) return 'Debe tener al menos 2 caracteres.';
  if (trimmed.length > 100) return 'Máximo 100 caracteres.';
  return null;
}

function CrearTipoConceptoModal({ isOpen, onClose, onCreated }) {
  const [values, setValues] = useState(INITIAL_VALUES);
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const acronymError = touched.acronym ? validateAcronym(values.acronym) : null;
  const nameError = touched.name ? validateName(values.name) : null;

  const handleClose = () => {
    if (isSubmitting) return;
    setValues(INITIAL_VALUES);
    setTouched({});
    setFeedback(null);
    onClose();
  };

  const handleChange = (field) => (event) => {
    const nextValue = field === 'acronym'
      ? event.target.value.slice(-1).toUpperCase()
      : event.target.value;
    setValues((prev) => ({ ...prev, [field]: nextValue }));
    if (feedback) setFeedback(null);
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const acronymErr = validateAcronym(values.acronym);
    const nameErr = validateName(values.name);
    setTouched({ acronym: true, name: true });
    if (acronymErr || nameErr) {
      setFeedback({
        type: 'error',
        message: acronymErr || nameErr,
      });
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);
    try {
      const result = await createTipoConcepto({
        tpConcepto: values.acronym.trim().toUpperCase(),
        deTipoConcepto: values.name.trim(),
      });
      if (result.ok) {
        setFeedback({
          type: 'success',
          message: 'Se creó el Tipo Concepto con éxito.',
        });
        setValues(INITIAL_VALUES);
        setTouched({});
        onCreated?.(result.data);
      } else {
        setFeedback({
          type: 'error',
          message: 'Error en el proceso de gestión de Tipo Concepto.',
        });
      }
    } catch {
      setFeedback({
        type: 'error',
        message: 'Error de conexión. Verifica tu red e intenta nuevamente.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Nuevo tipo de concepto"
      description="Crea una categoría para tus transacciones"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} isLoading={isSubmitting}>
            {isSubmitting ? 'Creando…' : 'Crear tipo de concepto'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <Field
          label="Nombre del Tipo Concepto"
          required
          htmlFor="de-tipo-concepto"
          error={touched.name ? nameError : undefined}
          hint="Descripción legible (ej. Ingreso, Egreso)"
        >
          <GlassInput
            id="de-tipo-concepto"
            label="Nombre del Tipo Concepto"
            value={values.name}
            onChange={handleChange('name')}
            onBlur={handleBlur('name')}
            isInvalid={Boolean(touched.name && nameError)}
            placeholder="Ej. Ingreso"
            maxLength={100}
          />
        </Field>
        <Field
          label="Acrónimo"
          required
          htmlFor="tp-concepto"
          error={touched.acronym ? acronymError : undefined}
          hint="Una sola letra mayúscula (I, E, A)"
        >
          <GlassInput
            id="tp-concepto"
            label="Acrónimo"
            value={values.acronym}
            onChange={handleChange('acronym')}
            onBlur={handleBlur('acronym')}
            isInvalid={Boolean(touched.acronym && acronymError)}
            placeholder="I"
            maxLength={1}
          />
        </Field>
        {feedback && (
          <div
            role="status"
            className={`flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm animate-fade-up ${feedback.type === 'success' ? 'border-emerald-300/30 bg-emerald-300/[0.06] text-emerald-100' : 'border-rose-300/30 bg-rose-300/[0.06] text-rose-100'}`}
          >
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/10 text-[11px] font-bold">
              {feedback.type === 'success' ? '✓' : '!'}
            </span>
            <p className="text-sm">{feedback.message}</p>
          </div>
        )}
      </form>
    </Modal>
  );
}

export default CrearTipoConceptoModal;